/**
 * Spec 041 — useFleet hook.
 *
 * Polls /api/equanaut/fleet every POLL_MS and exposes the fleet member list
 * with activity indicators for the Fleet tab (FR-004, FR-006).
 */

import { useCallback, useEffect, useRef, useState } from 'react';

const POLL_MS = 30_000;
const BASE = '/api/equanaut';

export type FleetStatus = 'active' | 'idle' | 'offline';
export type AgentType = 'comet' | 'cursor' | 'gateway';

export interface FleetMember {
  id: string;
  organizationId: string;
  agentId: string;
  agentType: AgentType;
  displayName: string | null;
  status: FleetStatus;
  lastHeartbeat: string | null;
  sessionId: string | null;
  sessionDisplayName: string | null;
  taskGoal: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

interface FleetListResponse {
  fleet: FleetMember[];
  polledAt: string;
}

interface ApiErrorResponse {
  error?: string;
}

function getErrorMessage(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback;
}

async function apiFetch<T>(path: string): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!res.ok) {
    const body = await res.json().catch((): ApiErrorResponse => ({}));
    throw new Error(body.error ?? `Request failed (${res.status})`);
  }
  return res.json();
}

export function useFleet(organizationId?: string, enabled = false) {
  const [fleet, setFleet] = useState<FleetMember[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const currentOrgRef = useRef<string | undefined>(organizationId);
  const requestSeqRef = useRef(0);

  const load = useCallback(async () => {
    if (!enabled || !organizationId) return;
    const requestOrg = organizationId;
    const requestSeq = requestSeqRef.current + 1;
    requestSeqRef.current = requestSeq;
    setIsLoading(true);
    setError(null);
    try {
      const org = encodeURIComponent(requestOrg);
      const data = await apiFetch<FleetListResponse>(
        `/fleet?organizationId=${org}`
      );
      if (currentOrgRef.current !== requestOrg || requestSeqRef.current !== requestSeq) return;
      setFleet(data.fleet);
    } catch (err: unknown) {
      if (currentOrgRef.current === requestOrg && requestSeqRef.current === requestSeq) {
        setError(getErrorMessage(err, 'Failed to load fleet'));
      }
    } finally {
      if (currentOrgRef.current === requestOrg && requestSeqRef.current === requestSeq) {
        setIsLoading(false);
      }
    }
  }, [enabled, organizationId]);

  // Initial load + 30-second poll (FR-006)
  useEffect(() => {
    if (!enabled) return;
    void load();
    const timer = setInterval(() => void load(), POLL_MS);
    return () => clearInterval(timer);
  }, [enabled, load]);

  // On org switch: reset (FR-015)
  useEffect(() => {
    currentOrgRef.current = organizationId;
    requestSeqRef.current += 1;
    setFleet([]);
    setIsLoading(false);
    setError(null);
  }, [organizationId]);

  return { fleet, isLoading, error, refresh: load };
}
